import { toast } from '@quinyx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deleteCustomerManagerToken,
  fetchCustomerManagerTokens,
  generateCustomerManagerToken,
  ICustomerManagerToken,
} from '@/api/endpoints/support/customer-manager-token';
import { getPaginatedCustomersApi } from '@/api/endpoints/customer';
import { ICustomerError } from '@/api/interfaces/customer';
import { useUser } from '@/context/authenticatedUser';
import i18n from '@/i18n';

const CUSTOMER_MANAGER_TOKENS_QUERY_KEY = ['customerManagerTokens'];

const getErrorMessage = (error: ICustomerError, fallback: string) =>
  error?.response?.data?.additionalErrorParameters?.exceptionMessage ||
  error?.response?.data?.message ||
  fallback;

const notifyError = (error: ICustomerError) =>
  toast({
    message: i18n.t(getErrorMessage(error, 'toast.somethingWentWrong')),
    type: 'negative',
  });

export const MAX_DOMAINS_FOR_DROPDOWN = 100;

export const useManageableDomainsCount = () =>
  useQuery({
    queryKey: ['customerManagerToken_domainsCount'],
    queryFn: () => getPaginatedCustomersApi('?size=1'),
    useErrorBoundary: false,
  });

export const useIsCustomerManager = () => {
  const { systemAdmin } = useUser();
  const { data, isLoading } = useManageableDomainsCount();

  return {
    isCustomerManager: !systemAdmin && (data?.totalElements ?? 0) > 0,
    isLoading: !systemAdmin && isLoading,
  };
};

export const useManageableDomains = (enabled: boolean) =>
  useQuery({
    queryKey: ['customerManagerToken_domains'],
    queryFn: () =>
      getPaginatedCustomersApi(
        `?size=${MAX_DOMAINS_FOR_DROPDOWN}&sort=name,asc`,
      ),
    useErrorBoundary: false,
    enabled,
  });

export const useSearchManageableDomains = (query: string) =>
  useQuery({
    queryKey: ['customerManagerToken_domainSearch', query],
    queryFn: () =>
      getPaginatedCustomersApi(
        `?name=${encodeURIComponent(query)}&size=50&sort=name,asc`,
      ),
    enabled: query.length > 0,
    useErrorBoundary: false,
  });

export const useCustomerManagerTokens = () =>
  useQuery<ICustomerManagerToken[], ICustomerError>({
    queryKey: CUSTOMER_MANAGER_TOKENS_QUERY_KEY,
    queryFn: fetchCustomerManagerTokens,
    useErrorBoundary: false,
    retry: false,
  });

export const useGenerateCustomerManagerToken = () => {
  const queryClient = useQueryClient();

  const {
    mutate: generateToken,
    mutateAsync: generateTokenAsync,
    ...rest
  } = useMutation({
    mutationFn: (domainId: number) => generateCustomerManagerToken(domainId),
    onSuccess: (data) => {
      queryClient.setQueryData<ICustomerManagerToken[]>(
        CUSTOMER_MANAGER_TOKENS_QUERY_KEY,
        (tokens = []) => [
          ...tokens.filter((existing) => existing.domainId !== data.domainId),
          data,
        ],
      );
    },
    onError: notifyError,
  });

  return { generateToken, generateTokenAsync, ...rest };
};

export const useDeleteCustomerManagerToken = () => {
  const queryClient = useQueryClient();

  const { mutate: deleteToken, ...rest } = useMutation({
    mutationFn: (domainId: number) => deleteCustomerManagerToken(domainId),
    onSuccess: (_data, domainId) => {
      queryClient.setQueryData<ICustomerManagerToken[]>(
        CUSTOMER_MANAGER_TOKENS_QUERY_KEY,
        (tokens = []) => tokens.filter((token) => token.domainId !== domainId),
      );
    },
    onError: notifyError,
  });

  return { deleteToken, ...rest };
};
